import React, { useState, useEffect, useMemo } from 'react';
import { API } from '../utils/helpers';
import FitTimePicker from './FitTimePicker';
import useMobileViewport from '../utils/useMobileViewport';

// Customer self-serve manage page: /booking/manage?token={uuid}
// Token-authenticated — no login. Reschedule or cancel a confirmed booking.

const BASE = {
  bg: '#f7f6f3', card: '#ffffff', border: '#e2e5ea', borderSoft: '#eef0f3',
  text: '#1a2233', sub: '#5b6472', muted: '#9aa4b2', disabled: '#cdd3db',
  accent: '#e67e22', accentTint: '#e67e2214', headerBg: '#0f1724', danger: '#c0392b', ok: '#27ae60',
};
let C = { ...BASE };

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW = ['S','M','T','W','T','F','S'];

function fmtDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function BookingManagePage() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [mode, setMode] = useState('view'); // view | reschedule | done-reschedule | done-cancel
  const [selectedDate, setSelectedDate] = useState(null);
  const [fitTime, setFitTime] = useState(null);       // a fitting's new time
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const token = useMemo(() => new URLSearchParams(window.location.search).get('token'), []);
  const fitQuery = useMemo(() => ({ token }), [token]);

  useMobileViewport();

  useEffect(() => {
    if (!token) { setLoadError(true); return; }
    fetch(`${API}/book/manage/${token}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        if (d.accentColor && /^#[0-9a-fA-F]{6}$/.test(d.accentColor)) {
          C = { ...BASE, accent: d.accentColor, accentTint: `${d.accentColor}14` };
        }
        setData(d);
      })
      .catch(() => setLoadError(true));
  }, [token]);

  const months = useMemo(() => {
    if (!data?.days) return [];
    const keys = Object.keys(data.days).sort();
    if (keys.length === 0) return [];
    const byMonth = {};
    keys.forEach(k => {
      const mk = k.slice(0, 7);
      if (!byMonth[mk]) byMonth[mk] = [];
      byMonth[mk].push(k);
    });
    return Object.keys(byMonth).sort().map(mk => {
      const [y, m] = mk.split('-').map(Number);
      return { year: y, month: m, firstDow: new Date(y, m - 1, 1).getDay(), daysInMonth: new Date(y, m, 0).getDate() };
    });
  }, [data]);

  const act = async (path, body, base = '/book/manage') => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}${base}/${token}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const d = await res.json();
      if (!res.ok) {
        setError(d.error || 'Something went wrong. Please call the shop.');
        setBusy(false);
        return false;
      }
      setBusy(false);
      return d;
    } catch (_) {
      setError('Something went wrong. Please call the shop.');
      setBusy(false);
      return false;
    }
  };

  const doReschedule = async () => {
    if (data.fit) {
      if (!fitTime) return;
      const result = await act('reschedule', { startsAt: fitTime.startsAt, fitterUserId: fitTime.fitterUserId }, '/book/fit/manage');
      if (result) {
        setData(prev => ({ ...prev, fit: { ...prev.fit, startsAt: result.startsAt, whenText: result.whenText, fitterName: result.fitterName } }));
        setMode('done-reschedule');
        window.scrollTo(0, 0);
      }
      return;
    }
    if (!selectedDate) return;
    const result = await act('reschedule', { dropoffDate: selectedDate });
    if (result) {
      setData(prev => ({ ...prev, booking: { ...prev.booking, dropoffDate: result.dropoffDate } }));
      setMode('done-reschedule');
      window.scrollTo(0, 0);
    }
  };

  const doCancel = async () => {
    if (!window.confirm('Cancel this booking?')) return;
    const result = await act('cancel');
    if (result) {
      setMode('done-cancel');
      window.scrollTo(0, 0);
    }
  };

  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '24px 12px', fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: 440, margin: '0 auto', background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,36,0.08)' }}>
        <div style={{ background: C.headerBg, padding: '22px 22px 18px', textAlign: 'center' }}>
          {data?.shopLogoUrl
            ? <img src={data.shopLogoUrl} alt={data?.shopName || ''} style={{ maxHeight: 56, maxWidth: 260, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            : <div style={{ fontSize: 17, fontWeight: 600, color: '#fff' }}>{data?.shopName || 'Your booking'}</div>}
          <div style={{ fontSize: 12, color: '#9aa4b2', marginTop: 6 }}>Manage your booking</div>
        </div>
        <div style={{ height: 3, background: C.accent }} />
        <div style={{ padding: 22 }}>{children}</div>
      </div>
      <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: C.muted }}>Powered by Spinboard</div>
    </div>
  );

  if (loadError) return shell(
    <div style={{ textAlign: 'center', padding: '30px 0' }}>
      <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>We couldn't find that booking.</div>
      <div style={{ fontSize: 13, color: C.sub }}>The link may be incorrect or expired. Please call the shop for help.</div>
    </div>
  );

  if (!data) return shell(
    <div style={{ textAlign: 'center', padding: '40px 0', color: C.sub, fontSize: 14 }}>Loading…</div>
  );

  const bk = data.booking;

  const summaryCard = (
    <div style={{ background: C.bg, borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
      <div style={{ fontSize: 14.5, fontWeight: 600, color: C.text }}>{bk.itemName}</div>
      {data.rental ? (
        <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>
          <strong style={{ color: C.text }}>{data.rental.whenText}</strong>
          {data.rental.bikes.length > 1 && <div style={{ marginTop: 2 }}>{data.rental.bikes.join(', ')}</div>}
        </div>
      ) : data.fit ? (
        <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>
          <strong style={{ color: C.text }}>{data.fit.whenText}</strong>{data.fit.fitterName ? ` with ${data.fit.fitterName}` : ''}
        </div>
      ) : (
        <div style={{ fontSize: 13, color: C.sub, marginTop: 4 }}>
          Drop-off: <strong style={{ color: C.text }}>{fmtDate(bk.dropoffDate)}</strong>
        </div>
      )}
      {bk.bikeDescription && !data.rental && <div style={{ fontSize: 12.5, color: C.sub, marginTop: 2 }}>{bk.bikeDescription}</div>}
    </div>
  );

  if (mode === 'done-reschedule') return shell(
    <div style={{ textAlign: 'center', padding: '10px 0' }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#eafaf1', color: C.ok, fontSize: 26, lineHeight: '52px', margin: '0 auto 14px' }}>✓</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 6 }}>Rescheduled!</div>
      <div style={{ fontSize: 14, color: C.sub, lineHeight: 1.6 }}>
        {data.fit
          ? <>Your fitting is now<br /><strong style={{ color: C.text }}>{data.fit.whenText}</strong>{data.fit.fitterName ? ` with ${data.fit.fitterName}` : ''}</>
          : <>Your new drop-off day is<br /><strong style={{ color: C.text }}>{fmtDate(bk.dropoffDate)}</strong></>}
      </div>
    </div>
  );

  if (mode === 'done-cancel') return shell(
    <div style={{ textAlign: 'center', padding: '10px 0' }}>
      <div style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 6 }}>Booking canceled</div>
      <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.6 }}>
        Sorry to see you go — if you'd like to rebook, we're at{' '}
        <a href={data.shopSlug ? `/booking/${data.shopSlug}` : '/booking'} style={{ color: C.accent }}>our booking page</a>
        {data.shopPhone ? <> or call {data.shopPhone}</> : null}.
      </div>
    </div>
  );

  if (!data.canManage) return shell(
    <>
      {summaryCard}
      <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.6 }}>
        {data.lockedReason === 'canceled' && 'This booking has been canceled.'}
        {data.lockedReason === 'arrived' && 'This bike has already been dropped off — changes are handled at the shop.'}
        {data.lockedReason === 'out' && 'Your bikes are out. Changes are handled at the shop.'}
        {data.lockedReason === 'window' && (data.rental ? `It's too close to your pick-up to make changes online. ` : data.fit ? `It's too close to your appointment to make changes online. ` : `It's too close to your drop-off day to make changes online. `)}
        {data.shopPhone && <> Please call <strong style={{ color: C.text }}>{data.shopPhone}</strong> and we'll help you out.</>}
      </div>
    </>
  );

  if (mode === 'reschedule' && data.fit) return shell(
    <>
      {summaryCard}
      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 10 }}>Pick a new day and time</div>
      <FitTimePicker query={fitQuery} C={C} value={fitTime} onChange={setFitTime} />
      {error && <div style={{ fontSize: 13, color: C.danger, margin: '10px 0' }}>{error}</div>}
      <button onClick={doReschedule} disabled={!fitTime || busy}
        style={{ width: '100%', background: C.accent, color: '#fff', border: 'none', padding: 13, borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: (!fitTime || busy) ? 0.5 : 1, fontFamily: 'inherit', margin: '14px 0 8px' }}>
        {busy ? 'Saving…' : 'Confirm new time'}
      </button>
      <button onClick={() => { setMode('view'); setFitTime(null); setError(''); }}
        style={{ width: '100%', background: 'transparent', color: C.sub, border: `1px solid ${C.border}`, padding: 11, borderRadius: 10, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
        Back
      </button>
    </>
  );

  if (mode === 'reschedule') return shell(
    <>
      {summaryCard}
      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 10 }}>Pick a new drop-off day</div>
      {months.map(({ year, month, firstDow, daysInMonth }) => (
        <div key={`${year}-${month}`} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>{MONTH_NAMES[month - 1]} {year}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
            {DOW.map((d, i) => <span key={i} style={{ fontSize: 11, color: C.muted, textAlign: 'center' }}>{d}</span>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
            {Array.from({ length: firstDow }).map((_, i) => <span key={`pad-${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const state = data.days[key];
              const sel = selectedDate === key;
              const base = { fontSize: 13, textAlign: 'center', padding: '8px 0', borderRadius: 8 };
              if (sel) return <span key={key} style={{ ...base, background: C.accent, color: '#fff', fontWeight: 600, cursor: 'pointer' }} onClick={() => setSelectedDate(null)}>{day}</span>;
              if (state === 'available') return <span key={key} style={{ ...base, border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer' }} onClick={() => setSelectedDate(key)}>{day}</span>;
              if (state === 'full') return <span key={key} style={{ ...base, color: C.muted, textDecoration: 'line-through' }}>{day}</span>;
              return <span key={key} style={{ ...base, color: C.disabled }}>{day}</span>;
            })}
          </div>
        </div>
      ))}
      {error && <div style={{ fontSize: 13, color: C.danger, marginBottom: 10 }}>{error}</div>}
      <button onClick={doReschedule} disabled={!selectedDate || busy}
        style={{ width: '100%', background: C.accent, color: '#fff', border: 'none', padding: 13, borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: (!selectedDate || busy) ? 0.5 : 1, fontFamily: 'inherit', marginBottom: 8 }}>
        {busy ? 'Saving…' : 'Confirm new day'}
      </button>
      <button onClick={() => { setMode('view'); setSelectedDate(null); setError(''); }}
        style={{ width: '100%', background: 'transparent', color: C.sub, border: `1px solid ${C.border}`, padding: 11, borderRadius: 10, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
        Back
      </button>
    </>
  );

  return shell(
    <>
      {summaryCard}
      {error && <div style={{ fontSize: 13, color: C.danger, marginBottom: 10 }}>{error}</div>}
      {!data.rental && (
        <button onClick={() => setMode('reschedule')}
          style={{ width: '100%', background: C.accent, color: '#fff', border: 'none', padding: 13, borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
          {data.fit ? 'Reschedule fitting' : 'Reschedule drop-off'}
        </button>
      )}
      {data.rental && (
        <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.6, marginBottom: 12 }}>
          To change your dates or bikes, call the shop{data.shopPhone ? <> at <strong style={{ color: C.text }}>{data.shopPhone}</strong></> : ''}. You can cancel online until {data.rental.windowHours} hours before pick-up.
        </div>
      )}
      <button onClick={doCancel} disabled={busy}
        style={{ width: '100%', background: 'transparent', color: C.danger, border: `1px solid ${C.border}`, padding: 11, borderRadius: 10, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
        {busy ? 'Working…' : data.rental ? 'Cancel rental' : 'Cancel booking'}
      </button>
      {data.fit && (
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginTop: 12 }}>
          You can move or cancel online until {data.fit.windowHours} hours before your appointment.
        </div>
      )}
      {!data.fit && !data.rental && data.cancellationPolicyText && (
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5, marginTop: 12, whiteSpace: 'pre-line' }}>{data.cancellationPolicyText}</div>
      )}
    </>
  );
}

export default BookingManagePage;